import type { IzvirnaDatoteka, StanjeIzvirnika } from '@koda/shared';
import { useEffect, useRef, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { datum, datumInUra, decimalno } from '../lib/format';
import { OpozorilaUvoza } from './OpozorilaUvoza';

const velikost = (bajtov: number): string => {
  const enote = ['B', 'kB', 'MB', 'GB'];
  let v = bajtov;
  let i = 0;
  while (v >= 1024 && i < enote.length - 1) {
    v /= 1024;
    i += 1;
  }
  const decimalk = i === 0 ? 0 : 1;
  return `${v.toLocaleString('sl-SI', { minimumFractionDigits: decimalk, maximumFractionDigits: decimalk, useGrouping: 'always' })} ${enote[i]!}`;
};

/** Kako je uvoz prenehal veljati, z besedami. */
const KONEC: Record<Exclude<StanjeIzvirnika, 'velja' | 'v-obdelavi'>, string> = {
  nadomescen: 'nadomeščen',
  zavrnjen: 'zavrnjen',
  'ni-uspel': 'uvoz ni uspel',
};

/** Do kdaj se izvirnik hrani, z besedami; rok teče od dne, ko uvoz preneha veljati. */
function hramba(d: IzvirnaDatoteka): string {
  if (d.izbrisano !== null) return `vsebina izbrisana ${datum(d.izbrisano)}`;
  if (d.stanje === 'velja') return 'brez roka (uvoz velja)';
  if (d.stanje === 'v-obdelavi') return 'rok se določi, ko se uvoz konča';
  if (d.prenehalo === null || d.hranjenoDo === null) return KONEC[d.stanje];
  return `${KONEC[d.stanje]} ${datum(d.prenehalo)}, hrani se do ${datum(d.hranjenoDo)}`;
}

/** Kako knjižbe uvoza hranijo izvirno vrstico, z besedami. */
const OBLIKA: Record<IzvirnaDatoteka['izvirneVrstice'], string> = {
  lokacija: 'lokacija vrstice v arhivu',
  vrednosti: 'vrednosti pri knjižbi',
  brez: 'brez knjižb',
};

/** Sklanjatev ob številu: 1 opozorilo, 2 opozorili, 3 opozorila, 5 opozoril (enako za napake). */
function sklon(n: number, oblike: [ena: string, dve: string, tri: string, vec: string]): string {
  const ostanek = n % 100;
  if (ostanek === 1) return oblike[0];
  if (ostanek === 2) return oblike[1];
  if (ostanek === 3 || ostanek === 4) return oblike[2];
  return oblike[3];
}
const OPOZORIL: [string, string, string, string] = ['opozorilo', 'opozorili', 'opozorila', 'opozoril'];
const NAPAK: [string, string, string, string] = ['napaka', 'napaki', 'napake', 'napak'];

/** Knjižbe uvoza: število, oblika izvirnih vrstic in zložena opozorila. */
function Uvoz({ d }: { d: IzvirnaDatoteka }): JSX.Element {
  const napak = d.opozorila.filter((o) => o.napaka).length;
  const stevilo = d.knjizb === null ? null : `${decimalno(String(d.knjizb), 0)} knjižb`;
  return (
    <>
      <div>
        {stevilo ?? '—'}
        {d.izvirneVrstice !== 'brez' && <span className="drobno"> · {OBLIKA[d.izvirneVrstice]}</span>}
      </div>
      {d.opozorila.length > 0 && (
        <details className="opozorila-uvoza">
          <summary>
            {String(d.opozorila.length)} {sklon(d.opozorila.length, OPOZORIL)}
            {napak > 0 ? `, od tega ${String(napak)} ${sklon(napak, NAPAK)}` : ''}
          </summary>
          <OpozorilaUvoza opozorila={d.opozorila} />
        </details>
      )}
    </>
  );
}

/**
 * Izvirne datoteke uvozov podjetja (docs/ARHITEKTURA.md, 5.6): vsaka
 * naložena datoteka je stisnjena shranjena, tudi ob ponovnem uvozu istega
 * leta. Samo skrbnik. `osvezitev` se spremeni ob koncu uvoza, da se seznam
 * prebere znova.
 */
export function IzvirneDatoteke({ podjetje, osvezitev }: { podjetje: string; osvezitev: string }): JSX.Element {
  const [datoteke, setDatoteke] = useState<IzvirnaDatoteka[] | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  // Velja samo odgovor zadnjega branja; starejši ne prepiše novejšega.
  const zadnje = useRef(0);

  useEffect(() => {
    const to = ++zadnje.current;
    api
      .izvirniki(podjetje)
      .then((d) => {
        if (to === zadnje.current) setDatoteke(d);
      })
      .catch((e: unknown) => {
        if (to === zadnje.current) setNapaka(e instanceof Error ? e.message : 'Seznama ni bilo mogoče prebrati.');
      });
  }, [podjetje, osvezitev]);

  return (
    <section className="kartica">
      <h2>Izvirne datoteke</h2>
      {napaka !== null && <p className="napaka">{napaka}</p>}
      {datoteke === null && napaka === null && <p className="drobno">Berem …</p>}
      {datoteke !== null && datoteke.length === 0 && (
        <p className="drobno">Izvirnih datotek še ni; shranijo se od te različice portala naprej.</p>
      )}
      {datoteke !== null && datoteke.length > 0 && (
        <div className="tabela-ovoj">
          <table className="nastavitve izvirniki">
            <thead>
              <tr>
                <th>Leto</th>
                <th>Vrsta</th>
                <th>Datoteka</th>
                <th>Naloženo</th>
                <th className="stevilka">Velikost</th>
                <th>Uvoz</th>
                <th>Hramba</th>
              </tr>
            </thead>
            <tbody>
              {datoteke.map((d) => (
                <tr key={d.id} className={d.stanje === 'velja' ? 'velja' : undefined}>
                  <td>{d.leto ?? '—'}</td>
                  <td>{d.vrsta === 'otvoritev' ? 'otvoritev' : d.vrsta === 'register' ? 'register OS' : 'glavna knjiga'}</td>
                  <td>
                    {d.izbrisano === null ? (
                      <a href={api.naslovIzvirnika(podjetje, d.id)} download={d.ime}>
                        {d.ime}
                      </a>
                    ) : (
                      <span title={d.razlogIzbrisa ?? undefined}>{d.ime}</span>
                    )}
                  </td>
                  <td>{datumInUra(d.nalozeno)}</td>
                  <td className="stevilka">{velikost(d.velikost)}</td>
                  <td>
                    <Uvoz d={d} />
                  </td>
                  <td>{hramba(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="namig">
        Ob vsakem uvozu se naložena datoteka stisnjena shrani, tudi ob ponovnem uvozu istega leta,
        da je mogoče kasneje preveriti, iz česa je nastalo že poslano poročilo. Datoteka uvoza, ki
        velja, nima roka izbrisa (izbriše se samo s podjetjem). Ko uvoz preneha veljati – ga
        nadomesti novejši, je zavrnjen ali ne uspe –, se datoteka hrani še dve leti od tega dne;
        nato se njena vsebina izbriše, zapis o tem pa ostane. Stolpec <em>Uvoz</em> pove, koliko
        knjižb je uvoz dal, kako hranijo izvirno vrstico (lokacija v arhivu ali vrednosti pri
        knjižbi) in katera opozorila je uvoz pokazal ob koncu.
      </p>
    </section>
  );
}
